/**
 * Data layer for the learning module.
 *
 * Every call degrades the same way the backend does: a disabled module returns
 * 404, an unreachable store returns 502, and both surface as "nothing here"
 * rather than an error screen. Review is a side activity — it must never look
 * broken just because the feature is off.
 */
import { useCallback, useEffect, useState } from "react";
import { authFetch } from "../../utils/auth";

const BASE = "/api/learning";

async function call(path, options) {
  const res = await authFetch(`${BASE}${path}`, options);
  if (res.status === 404) return null; // module disabled — not an error
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = body.detail || body.message;
    const msg = typeof detail === "string" ? detail : `Request failed (${res.status})`;
    throw new Error(msg);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/*  Trails                                                             */
/* ------------------------------------------------------------------ */

export const listTrails = (status = "draft", { search } = {}) => {
  const params = new URLSearchParams({ status });
  if (search) params.set("search", search);
  return call(`/trails?${params.toString()}`);
};

export const getTrail = (id) => call(`/trails/${id}`);

// The trail for a live session, keyed on the session id rather than the pk.
// Pure read of the stored trail — the per-turn capture keeps it current, so the
// symptom this returns is the engine's normalised statement of the problem and
// moves with the diagnosis. Use it anywhere the RAW first message would
// otherwise be shown as the symptom.
export const getTrailBySession = (sessionId) =>
  call(`/trails/by-session/${encodeURIComponent(sessionId)}`);

// A single string, not the whole trail — for UI that only needs the symptom
// and cannot afford the full trail's three-statement assembly (~1-2s measured
// in prod). One indexed SELECT server-side, plus an in-memory cache here so
// the SAME session never pays the network round trip twice in one page load —
// the correction composer, opened repeatedly from the same live chat, goes
// from a ~1-2s wait to instant on every open after the first.
//
// Stale-while-revalidate: callers use getSessionSymptomCached() for instant
// render of whatever the cache holds, then await getSessionSymptom() which
// ALWAYS hits the network (deduped) and returns the fresh value. The cache is
// updated on every successful fetch, so the next open is instant again.
const _symptomCache = new Map();

/**
 * Synchronous read of the last-resolved symptom for a session, or null.
 * Use this for instant initial render — it never triggers a fetch.
 */
export const getSessionSymptomCached = (sessionId) => {
  if (!sessionId) return null;
  const cached = _symptomCache.get(sessionId);
  // Only return resolved data, not in-flight promises.
  return cached && typeof cached.then !== "function" ? cached : null;
};

export const getSessionSymptom = async (sessionId) => {
  if (!sessionId) return null;

  const inflight = _symptomCache.get(sessionId);
  // A Promise means a fetch is already in flight — dedupe by returning it.
  if (inflight && typeof inflight.then === "function") return inflight;

  const pending = call(`/trails/symptom-by-session/${encodeURIComponent(sessionId)}`)
    .then((data) => {
      _symptomCache.set(sessionId, data);
      return data;
    })
    .catch((err) => {
      // A failure must not poison the cache — remove the promise, but keep
      // any previously resolved value so the next caller still renders
      // instantly while retrying.
      const prev = _symptomCache.get(sessionId);
      if (prev === pending) _symptomCache.delete(sessionId);
      throw err;
    });

  // Cache the promise so concurrent callers share this single request.
  _symptomCache.set(sessionId, pending);
  return pending;
};

export const updateTrail = (id, patch) =>
  call(`/trails/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });

/**
 * @deprecated — use updateTrail instead. Kept for TrailReview compat.
 *
 * `notes` ({ [stepNo]: text }) is OPTIONAL and only reaches the wire when it
 * holds something: a backend that predates ruled-out notes then receives
 * byte-for-byte the payload it receives today. An empty string is a real
 * value — it is how the engineer clears a note — so only an empty MAP is
 * dropped, never an empty entry.
 */
export const markSteps = (id, results, notes) =>
  call(`/trails/${id}/steps`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(
      notes && typeof notes === "object" && Object.keys(notes).length
        ? { results, notes }
        : { results }
    ),
  });

export const deleteStep = (id, stepNo) =>
  call(`/trails/${id}/steps/${stepNo}`, { method: "DELETE" });

export const reorderSteps = (id, order) =>
  call(`/trails/${id}/steps/reorder`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ order }),
  });

export const submitTrail = (id, outcome) =>
  call(`/trails/${id}/submit`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ outcome }),
  });

export const approveTrail = (id) =>
  call(`/trails/${id}/approve`, { method: "POST" });

export const rejectTrail = (id, note) =>
  call(`/trails/${id}/reject`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ note }),
  });

/** @deprecated alias — backend also accepts POST /trails/{id}/discard. */
export const publishTrail = (id, outcome) => submitTrail(id, outcome);

export const discardTrail = (id) =>
  call(`/trails/${id}/discard`, { method: "POST" });

/* ------------------------------------------------------------------ */
/*  Corrections                                                        */
/* ------------------------------------------------------------------ */

export const reportCorrection = (payload) =>
  call("/corrections", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

export const listCorrections = (status) => {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  return call(`/corrections${qs}`);
};

export const updateCorrection = (id, patch) =>
  call(`/corrections/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });

/* ------------------------------------------------------------------ */
/*  Insight                                                            */
/* ------------------------------------------------------------------ */

// Where engineers abandoned the suggested path, most frequent first — the
// ranked list of places the documented runbooks fail a real engineer.
// Admin-only on the backend (GET /api/learning/overrides), same gate as the
// review queues above.
export const listOverrideHotspots = (limit = 20) =>
  call(`/overrides?limit=${encodeURIComponent(limit)}`);

/* ------------------------------------------------------------------ */
/*  Hooks                                                              */
/* ------------------------------------------------------------------ */

/** Pending count for the sidebar badge. Polls submitted trails (admin inbox). */
export function usePendingCount(pollMs = 60000) {
  const [count, setCount] = useState(0);
  const [disabled, setDisabled] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const data = await listTrails("submitted");
      if (data === null) {
        setDisabled(true);
        setCount(0);
        return;
      }
      setCount(data.count ?? (data.trails || []).length);
    } catch {
      setCount(0);
    }
  }, []);

  useEffect(() => {
    if (disabled) return undefined;
    refresh();
    if (!pollMs) return undefined;
    const t = setInterval(refresh, pollMs);
    return () => clearInterval(t);
  }, [refresh, pollMs, disabled]);

  return { count, refresh, disabled };
}
